/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE. 
*/

import { FaqSectionFragment } from "@cosmos/hygraph-schema";

import {
	ButtonList,
	CosmosButtonProps,
	FAQ,
	ResponsiveContainer,
	SectionContainer,
	SectionHeading,
	BackgroundContainer,
} from "@components/elements";
import { getFaqId, getShortId } from "@components/utils";
import { cleanFAQItem, FAQItem } from "@components/utils/faq.utils";
import { css } from "@panda/css";
import { VStack } from "@panda/jsx";
import { useMemo } from "react";

const faqStyles = css({
	marginInline: "auto",
	maxWidth: "52rem",
	width: "100%",
	display: "flex",
	alignItems: "flex-start",
	flexDirection: "column",
	gap: "eds.dimension.32",
});

export const FAQSection = ({ config, items, title, id, faqButtons }: FaqSectionFragment): React.JSX.Element | null => {
	const faqItems: FAQItem[] = useMemo(() => items.map(cleanFAQItem), [items]);
	const faqId = useMemo(() => (id ? getFaqId(getShortId(id)) : undefined), [id]);
	const validHeading = Boolean(config?.heading?.title || config?.heading?.desc);
	const validContent = Boolean(items?.length || title || faqButtons?.length);
	if (!validHeading && !validContent) return null;

	if (!items || !items.length) return null;

	const cosmosButtons = faqButtons.map((button): CosmosButtonProps => {
		return {
			...button,
			size: "lg",
			analytics: {
				eventCategory: "button",
				interactionType: "click",
				eventAction: "navigation",
				eventLabel: button.name || button.label || "",
				eventValue: {
					component: "FAQ",
					section: "section",
				},
			},
		};
	});

	return (
		<BackgroundContainer
			bgAnimation={config?.backgroundAnimation}
			bgImageAsset={config?.backgroundImage}
			bgFullHeight={config?.backgroundFullHeight}
			edsBackgroundColor={config?.edsBackgroundColor}
		>
			<ResponsiveContainer>
				<SectionContainer>
					<VStack alignItems="stretch" gap="eds.dimension.48" className="faq-section">
						{validHeading ? <SectionHeading {...config?.heading} /> : null}
						{validContent ? (
							<VStack alignItems="stretch" gap="eds.dimension.32" id={faqId}>
								<div className={faqStyles}>
									<FAQ items={faqItems} title={title?.text} id={id} />
									{cosmosButtons?.length ? <ButtonList buttons={cosmosButtons} /> : null}
								</div>
							</VStack>
						) : null}
					</VStack>
				</SectionContainer>
			</ResponsiveContainer>
		</BackgroundContainer>
	);
};
